import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useShipmentTracking from '../../hooks/useShipmentTracking';
import { shipmentAPI } from '../../services/api';
import Timeline from './Timeline';
import TrackingMap from './TrackingMap';
import { useEffect } from 'react';

const EVENT_TYPES = [
  'BOOKING_CONFIRMED', 'DOCUMENTATION_READY', 'CONTAINER_LOADED',
  'DEPARTED_ORIGIN', 'IN_TRANSIT', 'ARRIVED_DESTINATION',
  'CUSTOMS_CLEARED', 'OUT_FOR_DELIVERY', 'DELIVERED',
  'CUSTOMS_HOLD', 'EXCEPTION'
];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const ShipmentTracking = () => {
  const { id } = useParams();
  const { tracking, milestones, events, eta, delays, loading, error, addEvent } = useShipmentTracking(id);

  const [shipment, setShipment] = useState(null);
  const [shipmentLoading, setShipmentLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ eventType: '', location: '', description: '' });

  useEffect(() => {
    const fetchShipment = async () => {
      try {
        const response = await shipmentAPI.getById(id);
        setShipment(response.data);
      } catch (err) {
        console.error('Error fetching shipment:', err);
      } finally {
        setShipmentLoading(false);
      }
    };
    fetchShipment();
  }, [id]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.eventType) return;

    const success = await addEvent(newEvent.eventType, {
      location: newEvent.location || undefined,
      description: newEvent.description || undefined
    });

    if (success) {
      setNewEvent({ eventType: '', location: '', description: '' });
      setShowAddEvent(false);
    }
  };

  if (shipmentLoading && loading) return <div className="loading">Loading tracking data...</div>;
  if (error) return <div className="error">{error}</div>;

  const confidenceLabel = (confidence) => {
    if (!confidence) return 'Unknown';
    const pct = Math.round(confidence * 100);
    if (pct >= 90) return `${pct}% (High)`;
    if (pct >= 70) return `${pct}% (Good)`;
    if (pct >= 50) return `${pct}% (Moderate)`;
    return `${pct}% (Low)`;
  };

  const confidenceClass = (confidence) => {
    if (!confidence) return '';
    if (confidence >= 0.9) return 'confidence-high';
    if (confidence >= 0.7) return 'confidence-good';
    if (confidence >= 0.5) return 'confidence-moderate';
    return 'confidence-low';
  };

  return (
    <div className="tracking-page">
      {/* Header */}
      <div className="page-header">
        <h1>
          Shipment Tracking
          {shipment && (
            <span className="tracking-shipment-id">
              {shipment.orderId || shipment.id?.slice(0, 8)}
            </span>
          )}
        </h1>
        <div className="header-actions">
          <Link to={`/shipments/${id}`} className="btn btn-secondary">Back to Shipment</Link>
          <button className="btn btn-primary" onClick={() => setShowAddEvent(!showAddEvent)}>
            {showAddEvent ? 'Cancel' : 'Add Event'}
          </button>
        </div>
      </div>

      {/* Shipment Summary */}
      {shipment && (
        <div className="tracking-summary">
          <div className="summary-item">
            <label>Route</label>
            <span>{shipment.originPort} → {shipment.destinationPort}</span>
          </div>
          <div className="summary-item">
            <label>Status</label>
            <span className={`badge badge-${shipment.status?.toLowerCase().replace(/_/g, '-')}`}>
              {shipment.status?.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="summary-item">
            <label>ETA</label>
            <span>
              {eta?.etaDate ? formatDate(eta.etaDate) : (shipment.etaDate ? formatDate(shipment.etaDate) : 'Not available')}
              {eta?.confidence != null && (
                <span className={`confidence-badge ${confidenceClass(eta.confidence)}`}>
                  {confidenceLabel(eta.confidence)}
                </span>
              )}
            </span>
          </div>
          {shipment.vesselName && (
            <div className="summary-item">
              <label>Vessel</label>
              <span>{shipment.vesselName}</span>
            </div>
          )}
          {shipment.currentLocation && (
            <div className="summary-item">
              <label>Current Location</label>
              <span>{shipment.currentLocation}</span>
            </div>
          )}
        </div>
      )}

      {/* Delay Alerts */}
      {delays && delays.totalDelayedMilestones > 0 && (
        <div className={`delay-alert delay-${delays.overallSeverity?.toLowerCase()}`}>
          <strong>Delay Alert ({delays.overallSeverity})</strong>
          <p>
            {delays.totalDelayedMilestones} milestone(s) delayed.
            Maximum delay: {delays.maxDelayHours} hours.
          </p>
        </div>
      )}

      {/* Add Event Form */}
      {showAddEvent && (
        <div className="add-event-form">
          <h3>Add Tracking Event</h3>
          <form onSubmit={handleAddEvent}>
            <div className="form-row">
              <div className="form-group">
                <label>Event Type *</label>
                <select
                  value={newEvent.eventType}
                  onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
                  required
                >
                  <option value="">Select event type...</option>
                  {EVENT_TYPES.map(type => (
                    <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  placeholder="e.g., Shanghai Port"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Optional description..."
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Event'}
            </button>
          </form>
        </div>
      )}

      <div className="tracking-grid">
        {/* Timeline */}
        <div className="tracking-section">
          <h2>Milestone Progress</h2>
          <Timeline milestones={milestones} />
        </div>

        {/* Map */}
        {shipment && (
          <div className="tracking-section">
            <h2>Shipment Location</h2>
            <TrackingMap
              originPort={shipment.originPort}
              destinationPort={shipment.destinationPort}
              currentLat={shipment.currentLatitude}
              currentLng={shipment.currentLongitude}
              currentLocation={shipment.currentLocation}
            />
          </div>
        )}
      </div>

      {/* Recent Events Table */}
      {events.length > 0 && (
        <div className="tracking-section full-width">
          <h2>Events ({events.length})</h2>
          <table className="simple-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Event</th>
                <th>Location</th>
                <th>Source</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {[...events].reverse().map(event => (
                <tr key={event.id} className={event.eventType === 'DELAY_DETECTED' ? 'row-warning' : ''}>
                  <td>{formatDateTime(event.eventDate)}</td>
                  <td>
                    <span className={`badge badge-event-${event.eventType?.toLowerCase()}`}>
                      {event.eventType?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>{event.location || '-'}</td>
                  <td>{event.source}</td>
                  <td>{event.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ShipmentTracking;
